import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Loader2, AtSign, Clock, Bell, Search, MapPin, X } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { containsProfanity, isReservedUsername, usernameHasProfanity } from '@/lib/profanity';
import { enablePush, disablePush, isSubscribedOnThisDevice, pushSupported } from '@/lib/push';

const ROLE_LEVELS = { user: 0, moderator: 1, admin: 2, owner: 3 };

export default function AccountSettingsDialog({ open, onOpenChange }) {
  const { user, setUser } = useAuth();
  const [username, setUsername] = useState('');
  const [saving, setSaving] = useState(false);

  // Notification preferences
  const [notifyEnabled, setNotifyEnabled] = useState(false);
  const [radius, setRadius] = useState(10);
  const [alertAddress, setAlertAddress] = useState('');
  const [alertLatLng, setAlertLatLng] = useState(null);
  const [addrResults, setAddrResults] = useState([]);
  const [searchingAddr, setSearchingAddr] = useState(false);
  const [deviceSubscribed, setDeviceSubscribed] = useState(false);
  const [enablingDevice, setEnablingDevice] = useState(false);

  useEffect(() => {
    if (open && user) {
      setUsername(user.username || '');
      setNotifyEnabled(!!user.notify_enabled);
      setRadius(user.notify_radius_miles || 10);
      setAlertAddress(user.alert_address || '');
      setAlertLatLng(user.alert_lat != null && user.alert_lng != null ? { lat: user.alert_lat, lng: user.alert_lng } : null);
      setAddrResults([]);
      isSubscribedOnThisDevice().then(setDeviceSubscribed);
    }
  }, [open, user]);

  const enableOnThisDevice = async () => {
    setEnablingDevice(true);
    try {
      const res = await enablePush();
      if (res.ok) { setDeviceSubscribed(true); toast.success('Push enabled on this device'); }
      else if (res.reason === 'denied') toast.error('Notifications are blocked for this site. Enable them in your browser/OS settings, then try again.', { duration: 8000 });
      else if (res.reason === 'unsupported') toast.error('This device/browser does not support push notifications.');
      else toast.error('Could not enable push on this device.');
    } finally { setEnablingDevice(false); }
  };

  const searchAddress = async () => {
    if (alertAddress.trim().length < 3) return;
    setSearchingAddr(true);
    try {
      const r = await api.get('/geocode', { params: { q: alertAddress } });
      setAddrResults(r.data || []);
    } finally { setSearchingAddr(false); }
  };

  const pickAddress = (r) => {
    setAlertAddress(r.label);
    setAlertLatLng({ lat: r.lat, lng: r.lng });
    setAddrResults([]);
  };

  const clearAddress = () => {
    setAlertAddress('');
    setAlertLatLng(null);
    setAddrResults([]);
  };

  const isStaff = (ROLE_LEVELS[user?.role] ?? 0) >= 2;
  const changed = username.trim() !== (user?.username || '');
  const formatOk = /^[A-Za-z0-9_-]{6,24}$/.test(username.trim());
  const isProfane = usernameHasProfanity(username);
  const isReserved = isReservedUsername(username) && !isStaff;

  // 48h cooldown
  let cooldownText = '';
  if (!isStaff && user?.username_changed_at) {
    const last = new Date(user.username_changed_at).getTime();
    const remaining = 48 * 3600 * 1000 - (Date.now() - last);
    if (remaining > 0) {
      const h = Math.floor(remaining / 3600000);
      const m = Math.floor((remaining % 3600000) / 60000);
      cooldownText = `You can change your username again in ${h}h ${m}m.`;
    }
  }
  const cooldownActive = !!cooldownText && changed;

  const validityHint = isProfane ? 'Please choose a username without inappropriate language.'
    : isReserved ? 'That username is reserved. Please choose another.'
    : cooldownText && changed ? cooldownText
    : '6–24 chars · letters, numbers, _ or - · changeable once every 48 hours';
  const canSave = changed && formatOk && !isProfane && !isReserved && !cooldownActive;

  const saveAll = async () => {
    if (changed && !canSave) { toast.error(validityHint); return; }
    setSaving(true);
    try {
      if (changed) {
        await api.patch('/me', { username });
      }
      const body = {
        notify_enabled: notifyEnabled,
        notify_radius_miles: radius,
        alert_address: alertAddress.trim(),
      };
      if (alertLatLng) { body.alert_lat = alertLatLng.lat; body.alert_lng = alertLatLng.lng; }
      const r = await api.patch('/me/preferences', body);
      setUser(r.data);
      // Keep this device's push subscription in sync with the toggle.
      if (notifyEnabled) {
        const res = await enablePush();
        if (res.ok) setDeviceSubscribed(true);
        else if (res.reason === 'denied') toast.message('Saved. Enable notifications in your browser/OS settings to get alerts when the app is closed.', { duration: 7000 });
      } else {
        await disablePush();
      }
      toast.success('Saved');
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="rounded-3xl max-w-md p-0 overflow-hidden glass-panel" data-testid="account-settings-dialog">
        <div className="p-6 sm:p-7 max-h-[85vh] overflow-y-auto scroll-soft">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">Account Settings</DialogTitle>
            <DialogDescription className="text-brand-slate">
              Your username is the only thing other neighbors see on your pins. Your name and Google photo are kept private.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-5 space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-brand-dim">Username</Label>
              <div className="mt-2 flex items-center rounded-xl bg-black/5 px-3 focus-within:bg-white focus-within:ring-2 focus-within:ring-brand">
                <AtSign className="w-4 h-4 text-brand-dim" />
                <Input
                  data-testid="username-input"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder="e.g. green_thumb_42"
                  maxLength={24}
                  className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </div>
              <div className={`text-[11px] mt-1 flex items-center gap-1 ${(username && !canSave && changed) ? 'text-[#C86F53]' : 'text-brand-dim'}`} data-testid="username-hint">
                {cooldownActive && <Clock className="w-3 h-3" />}{validityHint}
              </div>
            </div>

            <div className="rounded-2xl bg-black/5 px-3 py-2 text-xs text-brand-slate">
              <div className="font-medium text-brand-ink">Signed in as</div>
              <div>{user?.email}</div>
            </div>

            <div className="pt-4 border-t border-black/10 space-y-4" data-testid="notification-settings">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-brand-ink flex items-center gap-2"><Bell className="w-4 h-4 text-brand" /> Notify me about new nearby items</div>
                  <div className="text-xs text-brand-slate mt-0.5">Get alerted when new free items or yard sales are posted near you.</div>
                </div>
                <Switch data-testid="notify-enabled-switch" checked={notifyEnabled} onCheckedChange={setNotifyEnabled} />
              </div>

              {notifyEnabled && (
                <div className="space-y-4 pl-1">
                  {pushSupported() && !deviceSubscribed && (
                    <div className="rounded-xl bg-[#D99E4A]/15 border border-[#D99E4A]/30 px-3 py-2.5" data-testid="device-push-callout">
                      <div className="text-sm text-brand-ink font-medium">Turn on push for this device</div>
                      <div className="text-xs text-brand-slate mt-0.5">Push alerts are set per device. Enable it here to get notified on this phone/computer even when CurbAlert is closed.</div>
                      <Button
                        data-testid="enable-device-push-btn"
                        onClick={enableOnThisDevice} disabled={enablingDevice}
                        className="mt-2 h-8 rounded-full bg-brand-ink hover:bg-black text-white text-xs px-4"
                      >
                        {enablingDevice && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />} Enable on this device
                      </Button>
                    </div>
                  )}
                  {pushSupported() && deviceSubscribed && (
                    <div className="text-xs text-[#2C5545] font-medium flex items-center gap-1.5" data-testid="device-push-active">
                      <Bell className="w-3.5 h-3.5" /> Push notifications are active on this device.
                    </div>
                  )}
                  <div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs uppercase tracking-[0.2em] text-brand-dim">Alert radius</Label>
                      <span className="text-sm font-semibold text-brand-ink" data-testid="notify-radius-value">{radius} mi</span>
                    </div>
                    <Slider
                      data-testid="notify-radius-slider"
                      value={[radius]} min={1} max={20} step={1}
                      onValueChange={(v) => setRadius(v[0])}
                      className="mt-3"
                    />
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-brand-dim">Alert location (optional)</Label>
                    <div className="text-xs text-brand-dim mt-0.5">{alertLatLng ? 'Alerts use this address.' : 'Defaults to your current location. Set an address to watch a specific area.'}</div>
                    <div className="mt-2 flex gap-2">
                      <Input
                        data-testid="notify-address-input"
                        value={alertAddress}
                        onChange={e => setAlertAddress(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), searchAddress())}
                        placeholder="Search an address…"
                        className="rounded-xl bg-black/5 border-transparent focus:bg-white focus:border-brand"
                      />
                      {alertAddress ? (
                        <button type="button" data-testid="notify-address-clear" onClick={clearAddress} className="px-3 rounded-xl bg-black/10 text-brand-ink flex items-center"><X className="w-4 h-4" /></button>
                      ) : null}
                      <button type="button" data-testid="notify-address-search" onClick={searchAddress} className="px-3 rounded-xl bg-brand-ink text-white flex items-center">
                        {searchingAddr ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                      </button>
                    </div>
                    {addrResults.length > 0 && (
                      <ul className="mt-2 max-h-40 overflow-y-auto rounded-xl border border-black/5 bg-white scroll-soft" data-testid="notify-address-results">
                        {addrResults.map((r, i) => (
                          <li key={r.label || `addr-${i}`}>
                            <button type="button" data-testid={`notify-address-result-${i}`} onClick={() => pickAddress(r)} className="w-full text-left px-3 py-2 text-sm hover:bg-black/5 flex items-start gap-2">
                              <MapPin className="w-3.5 h-3.5 mt-0.5 text-brand" /> <span className="text-brand-ink">{r.label}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="pt-2 flex justify-end gap-2 border-t border-black/10">
              <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
              <Button
                data-testid="save-settings-btn"
                onClick={saveAll} disabled={saving || (changed && !canSave)}
                className="rounded-full bg-brand hover:bg-brand-hover text-white px-6"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Save
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
